// Medal artwork (original SVG, RouteZero purple → cyan), the Profile medal cabinet and the
// one-time "medal earned" pop-up. Medal state comes from src/core/medals.js.
import { h, svg } from './h.js';
import { play } from './sound.js';

// 24×24 line icons drawn for RouteZero (stroke only)
const ICONS = {
  headset: ['M4 15v-3a8 8 0 0 1 16 0v3', 'M4 15h3v5H5a1 1 0 0 1-1-1z', 'M20 15h-3v5h2a1 1 0 0 0 1-1z', 'M17 20c0 1.2-1.8 1.8-4 1.8'],
  nodes: ['M12 7.5 6.6 15.8', 'M12 7.5l5.4 8.3', 'M7.6 18h8.8', 'M12 2.7a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z', 'M5 15.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z', 'M19 15.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z'],
  switch: ['M3 8h18v8H3z', 'M6 12h1.5', 'M9.5 12H11', 'M13 12h1.5', 'M16.5 12H18', 'M7 8V5.5', 'M17 8V5.5'],
  router: ['M12 4a8 8 0 1 1 0 16 8 8 0 0 1 0-16z', 'M12 7v3.5', 'M10.3 8.7 12 7l1.7 1.7', 'M12 17v-3.5', 'M10.3 15.3 12 17l1.7-1.7', 'M7 12h3.5', 'M17 12h-3.5'],
  globe: ['M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18z', 'M3 12h18', 'M12 3c2.6 2.6 3.8 5.6 3.8 9s-1.2 6.4-3.8 9', 'M12 3c-2.6 2.6-3.8 5.6-3.8 9s1.2 6.4 3.8 9'],
  gear: ['M12 8.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z', 'M12 2.5v3', 'M12 18.5v3', 'M2.5 12h3', 'M18.5 12h3', 'M5.3 5.3l2.1 2.1', 'M16.6 16.6l2.1 2.1', 'M5.3 18.7l2.1-2.1', 'M16.6 7.4l2.1-2.1'],
  shield: ['M12 3l7 3v5.5c0 4.6-3 8.2-7 9.5-4-1.3-7-4.9-7-9.5V6z', 'M9 12l2.2 2.2L15.5 10'],
  clipboard: ['M9 3.5h6v3H9z', 'M7 5H5.5v15.5h13V5H17', 'M8.5 11h7', 'M8.5 14.5h7', 'M8.5 18h4'],
};

let uid = 0;
/** One medal (ribbon + disc + icon). Locked medals are drawn as a grey outline. */
export function medalArt(medal, { earned = true, size = 96 } = {}) {
  const id = `rzm${++uid}`;
  const icon = (ICONS[medal.icon] || ICONS.nodes).map((d) => svg('path', { d }));
  return svg('svg', { class: `rz-medal-art${earned ? '' : ' locked'}`, viewBox: '0 0 100 120', width: size, height: size * 1.2, 'aria-hidden': 'true', focusable: 'false' },
    svg('defs', {},
      svg('linearGradient', { id: `${id}d`, x1: '0', y1: '0', x2: '1', y2: '1' },
        svg('stop', { offset: '0', 'stop-color': earned ? '#8B5CF6' : '#CBD5E1' }),
        svg('stop', { offset: '1', 'stop-color': earned ? '#22D3EE' : '#E8EEF8' })),
      svg('linearGradient', { id: `${id}r`, x1: '0', y1: '0', x2: '0', y2: '1' },
        svg('stop', { offset: '0', 'stop-color': earned ? '#6D28D9' : '#C2CFE0' }),
        svg('stop', { offset: '1', 'stop-color': earned ? '#7C3AED' : '#D9E3F0' }))),
    // ribbon
    svg('path', { d: 'M30 2h16l-6 40H24z', fill: `url(#${id}r)` }),
    svg('path', { d: 'M54 2h16l6 40H60z', fill: earned ? '#22D3EE' : '#D9E3F0' }),
    svg('path', { d: 'M38 2h24v8H38z', fill: earned ? '#17213F' : '#C2CFE0' }),
    // disc: outer ridge, face, inner ring
    svg('circle', { cx: 50, cy: 76, r: 40, fill: earned ? '#17213F' : '#E8EEF8' }),
    svg('circle', { cx: 50, cy: 76, r: 37, fill: 'none', stroke: `url(#${id}d)`, 'stroke-width': 5, 'stroke-dasharray': earned ? '3.2 1.6' : '2 3' }),
    svg('circle', { cx: 50, cy: 76, r: 31, fill: `url(#${id}d)`, opacity: earned ? 1 : 0.55 }),
    svg('circle', { cx: 50, cy: 76, r: 26, fill: 'none', stroke: '#fff', 'stroke-opacity': earned ? 0.55 : 0.8, 'stroke-width': 1.5 }),
    earned ? svg('path', { d: 'M28 62a28 28 0 0 1 22-14', fill: 'none', stroke: '#fff', 'stroke-opacity': 0.45, 'stroke-width': 3, 'stroke-linecap': 'round' }) : null,
    svg('g', { transform: 'translate(36 62) scale(1.17)', fill: 'none', stroke: earned ? '#fff' : '#94A3B8', 'stroke-width': 1.8, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, ...icon));
}

const fmtDate = (t) => new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** Profile: all medals, earned in colour, the rest as outlines with what to do next. */
export function medalCabinet(status) {
  const earned = status.filter((m) => m.earned).length;
  return h('section', { class: 'rz-career-card panel rz-medals-card', id: 'rz-medals', 'data-testid': 'profile-medals', 'aria-labelledby': 'rz-medals-title', tabindex: '-1' },
    h('div', { class: 'rz-medals-head' },
      h('div', {}, h('div', { class: 'eyebrow' }, 'MEDAL CABINET'), h('h2', { id: 'rz-medals-title' }, 'Your medals')),
      h('span', { class: 'rz-medals-count', 'data-testid': 'profile-medals-count' }, `${earned} / ${status.length} earned`)),
    h('ul', { class: 'rz-medals' }, status.map((m) => h('li', { class: `rz-medal${m.earned ? ' earned' : ''}`, 'data-testid': `medal-${m.id}`, 'data-earned': m.earned ? 'true' : 'false' },
      medalArt(m, { earned: m.earned, size: 64 }),
      h('b', {}, m.name),
      h('span', { class: 'rz-medal-for' }, m.title),
      h('small', {}, m.earned ? (m.earnedAt ? `Earned ${fmtDate(m.earnedAt)}` : 'Earned') : `${m.goal} to earn this`)))));
}

const reduceMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const COLORS = ['#8B5CF6', '#22D3EE', '#F472B6', '#FACC15', '#7C3AED', '#67E8F9'];

/**
 * The one-time medal pop-up. Esc, the backdrop or “Continue” closes it; focus returns to where it was.
 * @param {object} medal from medalStatus()
 * @param {{ more?: number, next?: string|null, onCabinet?: () => void, onClose?: () => void }} o
 */
export function awardModal(medal, { more = 0, next = null, onCabinet, onClose } = {}) {
  const before = document.activeElement;
  const confetti = reduceMotion() ? null : h('div', { class: 'rz-award-confetti', 'aria-hidden': 'true' },
    Array.from({ length: 28 }, (_, i) => h('i', { style: `left:${(i * 37) % 100}%;background:${COLORS[i % COLORS.length]};animation-delay:${(i % 7) * 60}ms;--rz-drift:${((i * 53) % 120) - 60}px;--rz-turn:${(i * 97) % 540}deg` })));
  const close = () => { if (!wrap.isConnected) return; wrap.remove(); document.removeEventListener('keydown', onKey, true); if (before && document.contains(before)) before.focus?.(); onClose?.(); };
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    if (e.key === 'Tab') { // keep focus inside the dialog
      const f = [...wrap.querySelectorAll('button')]; const i = f.indexOf(document.activeElement);
      e.preventDefault(); f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
    }
  };
  const cont = h('button', { class: 'btn primary', type: 'button', 'data-testid': 'award-continue', onclick: close }, 'Continue');
  const wrap = h('div', { class: 'rz-award-wrap', 'data-testid': 'award-modal', onclick: (e) => { if (e.target === wrap) close(); } },
    confetti,
    h('div', { class: 'rz-award', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'rz-award-title', 'aria-describedby': 'rz-award-desc' },
      h('div', { class: 'rz-award-glow', 'aria-hidden': 'true' }),
      h('div', { class: 'rz-award-medal' }, medalArt(medal, { size: 132 })),
      h('div', { class: 'rz-award-kicker' }, 'MEDAL EARNED'),
      h('h2', { id: 'rz-award-title', 'data-testid': 'award-title' }, medal.name),
      h('p', { id: 'rz-award-desc' }, medal.kind === 'support' ? 'You completed the Level 1 Support Desk — all five tickets, verified.' : `You completed ${medal.title} — every mission, verified.`),
      next ? h('p', { class: 'rz-award-next', 'data-testid': 'award-next' }, `What’s next: ${next}`) : null,
      more > 0 ? h('p', { class: 'rz-award-more' }, `+${more} more medal${more === 1 ? '' : 's'} waiting in your cabinet`) : null,
      h('div', { class: 'rz-award-actions' },
        h('button', { class: 'btn ghost', type: 'button', 'data-testid': 'award-cabinet', onclick: () => { close(); onCabinet?.(); } }, 'See my medals'),
        cont)));
  document.body.append(wrap);
  play('medal');
  document.addEventListener('keydown', onKey, true);
  cont.focus();
  return { el: wrap, close };
}

/** Small corner pop-up: “+100 XP”, or a level-up banner. Fades out by itself; never blocks the page. */
export function rewardToast({ xp = 0, level = null }) {
  let stack = document.querySelector('.rz-reward-stack');
  if (!stack) { stack = h('div', { class: 'rz-reward-stack', role: 'status', 'aria-live': 'polite' }); document.body.append(stack); }
  const t = h('div', { class: `rz-reward-toast${level ? ' level' : ''}`, 'data-testid': level ? 'levelup-toast' : 'xp-toast' },
    level ? [h('span', { class: 'rz-reward-k' }, 'LEVEL UP'), h('b', {}, `Level ${String(level).padStart(2, '0')} reached!`)] : h('b', {}, `+${xp.toLocaleString()} XP`));
  stack.append(t);
  play(level ? 'levelup' : 'chime');
  requestAnimationFrame(() => t.classList.add('show'));
  setTimeout(() => { t.classList.remove('show'); setTimeout(() => t.remove(), 400); }, level ? 4200 : 2600);
  return t;
}
